"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { trackContactFormSubmitSuccess } from "@/lib/analytics/gtm-lead-metadata";
import { gatherClientDiagnostics, supportDiagnosticsFields } from "@/lib/feedback/client-diagnostics";
import { composeSupportMessage } from "@/lib/support/support-handoff-text";
import { useSubmissionKey } from "@/lib/support/use-submission-key";
import type { ContactPageFormValues } from "@/lib/validation/schemas";
import type { SupportHandoff } from "./support-handoff";
import type { useContactFormPrefill } from "./use-contact-form-prefill";
import { SMS_CONSENT_VERSION } from "@shared/legal/sms";

type ContactFormPrefill = ReturnType<typeof useContactFormPrefill>;

function submitErrorMessage(err: unknown): string {
  const message = err instanceof Error ? err.message : "";
  if (message.includes("Too many requests")) return "Please wait a moment before submitting again.";
  if (message.includes("valid business email")) return "Please use a valid business email address.";
  return "Something went wrong. Try again, or call us.";
}

function splitName(name: string) {
  const [firstName = "", ...rest] = name.trim().split(/\s+/);
  return { firstName, lastName: rest.join(" ") };
}

function requestMetadata(
  value: ContactPageFormValues,
  pageUrl: string,
  prefill: ContactFormPrefill,
  handoff: SupportHandoff | null,
) {
  return {
    submitted_from: "contact_form",
    inquiry_type: value.inquiryType,
    page_path: pageUrl,
    ...splitName(value.name),
    email: value.email,
    phone: value.phone || null,
    companyName: value.inquiryType === "sales" ? value.company || null : null,
    orderReference: value.inquiryType === "order_issue" ? value.orderReference || null : null,
    smsConsent: value.smsConsent ?? false,
    smsMarketingConsent: false,
    smsTransactionalConsent: value.smsConsent ?? false,
    smsConsentVersion: SMS_CONSENT_VERSION,
    ...(handoff?.threadId && { ai_thread_id: handoff.threadId }),
    ...(prefill.pricingPlan && { pricing_plan: prefill.pricingPlan }),
    ...(prefill.typeParam && { type_param: prefill.typeParam }),
    ...(prefill.isEnterpriseLead && { is_enterprise_lead: true }),
  };
}

export function useContactFormSubmit(prefill: ContactFormPrefill, handoff: SupportHandoff | null) {
  const router = useRouter();
  const createSupportRequest = useMutation(api.support.mutations.createRequest);
  const submissionKey = useSubmissionKey();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");

  async function submit(value: ContactPageFormValues) {
    setSubmitError(null);
    try {
      const diagnostics = gatherClientDiagnostics();
      await createSupportRequest({
        contactEmail: value.email,
        issueType: value.inquiryType,
        message: composeSupportMessage(value.message, handoff?.transcript ?? ""),
        pagePath: diagnostics.url ?? "",
        sourcePath: diagnostics.path,
        sourceReferrer: diagnostics.referrer,
        metadata: requestMetadata(value, diagnostics.url ?? "", prefill, handoff),
        honeypot: honeypot || undefined,
        ...supportDiagnosticsFields(diagnostics),
        submissionKey: submissionKey.current(),
      });
      submissionKey.reset();
      trackContactFormSubmitSuccess({ formId: "contact-form", inquiryType: value.inquiryType });
      router.push("/thank-you");
    } catch (err) {
      setSubmitError(submitErrorMessage(err));
    }
  }

  return { submit, submitError, honeypot, setHoneypot };
}
